import Link from "next/link";
import LazyImage from "@/my_components/Common/LazyImage";
import { useEventImpression } from "@/my_components/Common/useEventImpression";
import { getEventPriceSummary } from "@/lib/eventPricing";
import { ROUTE } from "@/route/app_routes.js";

const stripHtml = (html) =>
  (html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const formatDateLabel = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T${timeStr || "00:00"}`);
  const datePart = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  return timeStr ? `${datePart} · ${timeStr}` : datePart;
};

/**
 * The ONE event card used everywhere on the platform -- homepage carousels, the /events grid,
 * dashboard widgets, and the event-creation wizard's own live preview (see EventLivePreview.js,
 * which renders this exact component against in-progress, unsaved form state). Previously each
 * surface had its own bespoke card markup; this replaces all of them so a design change only
 * ever needs to happen once, and so the wizard's preview is a true, byte-for-byte preview of
 * what the real card actually looks like, not an approximation.
 *
 * `event` takes the real API shape (EventResource) when `isPreview` is false, or a partial
 * synthetic object built from live form state when `isPreview` is true -- either way, every
 * field read below is read defensively (optional chaining / fallbacks), since the preview case
 * deliberately won't have an `id`, a real `organizer`, etc. yet.
 *
 * `href` overrides the default public details-page link (e.g. the student dashboard threads a
 * `returnTo` query param through so the back button lands on the right tab). `footer` overrides
 * the default price + "Get tickets" row entirely -- e.g. the dashboard's "My Events"/"History"
 * tabs show Edit/Bookings/Repurpose actions instead of a ticket CTA. When `footer` is supplied,
 * only the cover+body area is wrapped in the navigation link (not the whole card), since the
 * footer then holds its own separate links/buttons and HTML doesn't allow nested <a> tags.
 */
const EventCard = ({ event, isPreview = false, href, footer }) => {
  const impressionRef = useEventImpression(isPreview ? null : event?.id);
  if (!event) return null;

  const priceSummary = getEventPriceSummary(event, { freeLabel: "Free", fromLabel: "From" });
  const dateLabel = formatDateLabel(event.startDate, event.startTime);
  const venueLabel = event.venueName || (event.attendanceType === "online" ? "Online event" : event.attendanceType === "both" ? "In person & online" : null);
  const descriptionSnippet = stripHtml(event.summary) || stripHtml(event.description);
  const organizerName = event.organizer?.name || event.organizerName || "Organizer name";
  const resolvedHref = href || `${ROUTE.event_details}${event.id}`;

  const defaultFooter = (
    <div className="rbt-event-card-v2__footer">
      <span className={`rbt-event-card-v2__price ${priceSummary.isFree ? "is-free" : ""}`}>{priceSummary.label}</span>
      <span className="rbt-event-card-v2__cta">Get tickets</span>
    </div>
  );

  const CardMain = (
    <>
      <div className="rbt-event-card-v2__cover">
        {event.videoEmbedId ? (
          <iframe
            src={`https://www.youtube.com/embed/${event.videoEmbedId}?mute=1&autoplay=0`}
            title={event.title || "Event video preview"}
            allow="encrypted-media"
          />
        ) : event.imageUrl ? (
          <LazyImage src={event.imageUrl} alt={event.title || "Event cover"} fill sizes="(max-width: 575px) 92vw, (max-width: 991px) 45vw, 30vw" unoptimized={isPreview} />
        ) : (
          <div className="rbt-event-card-v2__cover-empty">
            <i className="feather-image"></i>
          </div>
        )}
      </div>

      <div className="rbt-event-card-v2__body">
        <h3 className="rbt-event-card-v2__title">{event.title || "Your event title"}</h3>

        <div className="rbt-event-card-v2__organizer">
          <span className="rbt-event-card-v2__icon"><i className="feather-user"></i></span>
          By {organizerName}
        </div>

        {venueLabel && (
          <div className="rbt-event-card-v2__row">
            <i className="feather-map-pin"></i> {venueLabel}
          </div>
        )}
        {dateLabel && (
          <div className="rbt-event-card-v2__row">
            <i className="feather-calendar"></i> {dateLabel}
          </div>
        )}

        <div className="rbt-event-card-v2__overview">
          <strong>Overview</strong>
          <p>{descriptionSnippet || "A short and sweet sentence about your event."}</p>
          {event.eventsType && <span className="rbt-event-card-v2__category">Category: {event.eventsType}</span>}
        </div>

        {!footer && defaultFooter}
      </div>
    </>
  );

  if (isPreview) {
    return (
      <div className="rbt-event-card-v2 is-preview">
        {CardMain}
        {footer && <div className="rbt-event-card-v2__body-footer">{footer}</div>}
      </div>
    );
  }

  if (footer) {
    return (
      <div className="rbt-event-card-v2">
        <Link className="rbt-event-card-v2__link" href={resolvedHref} ref={impressionRef}>
          {CardMain}
        </Link>
        <div className="rbt-event-card-v2__body-footer">{footer}</div>
      </div>
    );
  }

  return (
    <Link className="rbt-event-card-v2" href={resolvedHref} ref={impressionRef}>
      {CardMain}
    </Link>
  );
};

export default EventCard;
